import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Button, Chip, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { Close } from "@mui/icons-material";
import { authData } from "../../../components/GetAuth";
import { formatGBP2 } from "../../../components/Features";
import popupStyles from "../../../components/popups/Popup.module.css";
import styles from "../shop.module.css";
import { PAYMENT_METHOD_LABEL, progressColor } from "./orderStatus";

// Order detail with the admin actions: confirm, cancel (restocks), record a payment
const OrderDetailsPopup = ({ orderId, onClose, onUpdated, notify }) => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("");
  const [reason, setReason] = useState("");
  const [payment, setPayment] = useState({ amount: "", payment_method: "Cash", note: "" });

  useEffect(() => {
    axios.get(process.env.REACT_APP_API_URL + "v1/shop/orders/" + orderId, {
      headers: { Authorization: `Bearer ${API_TOKEN}` },
    }).then((response) => {
      if (response.data.status === "SUCCESS") {
        setOrder(response.data.data);
        const dueAmount = Number(response.data.data.total) - Number(response.data.data.paid_amount);
        setPayment((prev) => ({ ...prev, amount: dueAmount > 0 ? dueAmount.toFixed(2) : "" }));
      }
    }).catch(() => { });
  }, [orderId, API_TOKEN]);

  const post = (path, data, done) => {
    setBusy(true);
    axios.post(process.env.REACT_APP_API_URL + "v1/shop/orders/" + orderId + path, data, {
      headers: { Authorization: `Bearer ${API_TOKEN}` },
    }).then((response) => {
      setBusy(false);
      if (response.data.status === "SUCCESS") {
        setOrder(response.data.data);
        setMode("");
        onUpdated(response.data.data);
        notify(done, "success");
      } else {
        notify(response.data.message, "error");
      }
    }).catch(() => {
      setBusy(false);
      notify("Something went wrong. Please try again.", "error");
    });
  };

  const isCancelled = order?.progress === "Cancelled";
  const isDelivered = order?.progress === "Delivered";
  const due = order ? Number(order.total) - Number(order.paid_amount) : 0;

  return (<>
    <DialogTitle className={`${popupStyles.PopupTitleBar}`}>
      {order ? `Order #${order.order_no}` : "Order"}
      <IconButton onClick={onClose} className={`${popupStyles.PopupCloseBU}`} aria-label="Close"><Close /></IconButton>
    </DialogTitle>
    <DialogContent className={`${popupStyles.PopupContent}`}>
      {!order && <p>Loading…</p>}
      {order && (<>
        <div className={`${styles.DetailGrid}`}>
          <div className={`${styles.DetailBox}`}>
            <h4>Order</h4>
            <div>Placed {dayjs(order.created_at).format("DD MMM YYYY, HH:mm")}</div>
            <div>Status <Chip size="small" label={order.progress} className={`${styles.ProgressChip}`} style={{ background: progressColor(order.progress) }} /></div>
            <div>Placed by {order.source === "admin" ? `our team${order.added_by?.name ? " (" + order.added_by.name + ")" : ""}` : "the customer online"}</div>
            <div>Delivery date {order.delivery_date ? dayjs(order.delivery_date).format("DD MMM YYYY") : "-"}</div>
            {order.cancel_reason && <div>Cancel reason: {order.cancel_reason}</div>}
          </div>
          <div className={`${styles.DetailBox}`}>
            <h4>Deliver to</h4>
            <div><strong>{order.delivery_address?.name || order.customer?.name}</strong></div>
            <div>{order.delivery_address?.address}</div>
            <div>{order.delivery_address?.post_code}</div>
            <div>{order.delivery_address?.phone}</div>
          </div>
          <div className={`${styles.DetailBox}`}>
            <h4>Customer</h4>
            <div>{order.customer?.name} ({order.customer?.customer_id})</div>
            <div>{order.customer?.email}</div>
            <div>{order.customer?.phone}</div>
          </div>
          <div className={`${styles.DetailBox}`}>
            <h4>Payment</h4>
            <div>{PAYMENT_METHOD_LABEL[order.payment_method] || order.payment_method}</div>
            <div>{order.payment_status === 1 ? <Chip size="small" color="success" label="Paid" /> : <Chip size="small" color="error" label={`Due ${formatGBP2(due)}`} />}</div>
            {order.payments?.length > 0 && order.payments.map((p) => (<div key={p.id} className={`${styles.SubText}`}>{dayjs(p.created_at).format("DD/MM/YYYY")}: {formatGBP2(p.amount)} {p.payment_method ? `(${p.payment_method})` : ""}</div>))}
          </div>
        </div>

        {order.notes && <div className={`${styles.DetailBox}`} style={{ marginBottom: 16 }}><h4>Customer notes</h4>{order.notes}</div>}

        <table className={`${styles.ItemsTable}`}>
          <thead>
            <tr><th>Item</th><th className={`${styles.Right}`}>Price</th><th className={`${styles.Right}`}>Qty</th><th className={`${styles.Right}`}>Total</th></tr>
          </thead>
          <tbody>
            {order.items?.map((item) => (<tr key={item.id}>
              <td><span className={`${styles.ItemCell}`}>{item.product?.image && <img src={item.product.image} alt="" className={`${styles.Thumb}`} />}{item.title}</span></td>
              <td className={`${styles.Right}`}>{formatGBP2(item.price)}</td>
              <td className={`${styles.Right}`}>{item.quantity}</td>
              <td className={`${styles.Right}`}>{formatGBP2(item.sub_amount)}</td>
            </tr>))}
          </tbody>
        </table>
        <div className={`${styles.Totals}`}>
          <div><span>Items</span><span>{formatGBP2(order.subtotal)}</span></div>
          <div><span>Delivery</span><span>{formatGBP2(order.delivery_charge)}</span></div>
          <div className={`${styles.Grand}`}><span>Total</span><span>{formatGBP2(order.total)}</span></div>
          <div><span>Paid</span><span>{formatGBP2(order.paid_amount)}</span></div>
        </div>

        {mode === "cancel" && (<div className={`${popupStyles.FormMainRow}`} style={{ marginTop: 16 }}>
          <div className={`${popupStyles.FormGroupFull}`}>
            <TextField label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} fullWidth />
            <span className={`${popupStyles.HintText}`}>Stock is put back and the delivery booking is cancelled.</span>
          </div>
        </div>)}

        {mode === "payment" && (<div className={`${popupStyles.FormMainRow}`} style={{ marginTop: 16 }}>
          <div className={`${popupStyles.FormGroupHalf}`}>
            <TextField label="Amount (£)*" type="number" value={payment.amount} onChange={(e) => setPayment((p) => ({ ...p, amount: e.target.value }))} fullWidth inputProps={{ min: 0, step: "0.01" }} />
          </div>
          <div className={`${popupStyles.FormGroupHalf}`}>
            <FormControl fullWidth>
              <InputLabel>Payment Method*</InputLabel>
              <Select label="Payment Method*" value={payment.payment_method} onChange={(e) => setPayment((p) => ({ ...p, payment_method: e.target.value }))}>
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                <MenuItem value="Card Payment">Card Payment</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className={`${popupStyles.FormGroupFull}`}>
            <TextField label="Note" value={payment.note} onChange={(e) => setPayment((p) => ({ ...p, note: e.target.value }))} fullWidth />
          </div>
        </div>)}

        <div className={`${styles.DetailActions}`}>
          {mode === "" && <>
            {order.progress === "Placed" && <Button className={`${popupStyles.SubmitPopupBU}`} disabled={busy} onClick={() => post("/status", { status: 1 }, "Order confirmed.")}>Confirm Order</Button>}
            {!isCancelled && !isDelivered && order.booking_status <= 1 && <Button className={`${popupStyles.CancelPopupBU}`} disabled={busy} onClick={() => navigate("/booking/edit-delivery-booking/" + order.booking_id)}>Edit Order</Button>}
            {!isCancelled && order.payment_status !== 1 && <Button className={`${popupStyles.CancelPopupBU}`} disabled={busy} onClick={() => setMode("payment")}>Record Payment</Button>}
            {!isCancelled && !isDelivered && <Button className={`${popupStyles.CancelPopupBU}`} disabled={busy} onClick={() => setMode("cancel")}>Cancel Order</Button>}
          </>}
          {mode === "cancel" && <>
            <Button className={`${popupStyles.CancelPopupBU}`} disabled={busy} onClick={() => setMode("")}>Back</Button>
            <Button className={`${popupStyles.SubmitPopupBU}`} disabled={busy} onClick={() => post("/status", { status: -1, reason }, "Order cancelled.")}>Cancel Order</Button>
          </>}
          {mode === "payment" && <>
            <Button className={`${popupStyles.CancelPopupBU}`} disabled={busy} onClick={() => setMode("")}>Back</Button>
            <Button className={`${popupStyles.SubmitPopupBU}`} disabled={busy || !(Number(payment.amount) > 0)} onClick={() => post("/payment", payment, "Payment recorded.")}>Save Payment</Button>
          </>}
        </div>
      </>)}
    </DialogContent>
  </>);
};

export default OrderDetailsPopup;
